// Fonctions utilitaires pour le formatage des nombres et dates

/**
 * Formate un nombre avec le nombre de décimales spécifié
 * @param {number} value - Valeur à formater
 * @param {number} decimals - Nombre de décimales (défaut: 2)
 * @returns {string} - Nombre formaté
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return '0';
  return parseFloat(value).toFixed(decimals).replace(/\.?0+$/, '');
};

/**
 * Formate une date en format français
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée (ex: "15/01/2025")
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('fr-FR');
};

/**
 * Formate une date avec l'heure en format français
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée (ex: "15/01/2025 14:30")
 */
export const formatDateTime = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('fr-FR');
};

/**
 * Formate un montant selon la devise choisie
 * @param {number} amount - Montant à formater
 * @param {string} devise - Code devise ('EUR' ou 'CAD')
 * @returns {string} - Montant formaté (ex: "123,45 €" ou "$123.45")
 */
export const formatCurrency = (amount, devise = 'EUR') => {
  if (amount === null || amount === undefined) {
    return devise === 'CAD' ? '$0.00' : '0,00 €';
  }
  const num = parseFloat(amount).toFixed(2);
  if (devise === 'CAD') {
    return `$${num}`;
  }
  // EUR: virgule comme séparateur décimal, symbole à droite
  return `${num.replace('.', ',')} €`;
};

/**
 * Retourne uniquement le symbole de la devise
 * @param {string} devise - Code devise ('EUR' ou 'CAD')
 * @returns {string} - Symbole (€ ou $)
 */
export const getCurrencySymbol = (devise = 'EUR') => {
  return devise === 'CAD' ? '$' : '€';
};

/**
 * Ajuste la luminosité d'une couleur hexadécimale
 * @param {string} color - Couleur en format hex (#RRGGBB)
 * @param {number} amount - Montant d'ajustement (-255 à 255)
 * @returns {string} - Couleur ajustée
 */
export const adjustColor = (color, amount) => {
  const usePound = color[0] === '#';
  const col = usePound ? color.slice(1) : color;
  const num = parseInt(col, 16);
  let r = (num >> 16) + amount;
  let g = (num >> 8 & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;
  r = r > 255 ? 255 : r < 0 ? 0 : r;
  g = g > 255 ? 255 : g < 0 ? 0 : g;
  b = b > 255 ? 255 : b < 0 ? 0 : b;
  return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
};

/**
 * Traduit un statut de commande en français
 * @param {string} statut - Statut de la commande
 * @returns {string} - Statut traduit
 */
export const translateStatut = (statut) => {
  const statuts = {
    'brouillon': 'Brouillon',
    'en_cours': 'En cours',
    'livre': 'Livrée',
    'facture': 'Facturée',
    'annule': 'Annulée'
  };
  return statuts[statut] || 'En cours';
};

/**
 * Traduit un type de client en français
 * @param {string} type - Type de client
 * @returns {string} - Type traduit
 */
export const translateClientType = (type) => {
  const types = {
    'particulier': 'Particulier',
    'professionnel': 'Professionnel',
    'association': 'Association',
    'GMS': 'GMS',
    'Restaurant': 'Restaurant',
    'Magasin': 'Magasin'
  };
  return types[type] || type;
};
